import { useState } from 'react';

import { CopyOutlined } from '@ant-design/icons';
import { Button, notification } from 'antd';

import { Attendance } from 'components/Pages/Admin/CheckinPage/QrCodeScanner/types';
import { format } from 'date-fns/format';
import attendanceListService from 'services/attendanceList';

import styles from './styles.module.css';

type AttendanceCardProps = {
  staffToken: string;
  attendance: Attendance;
  onUndoCheckin?: () => void;
  onCheckin?: () => void;
  onUpdateAttendance?: (attendance: Attendance) => void;
  disabledAction?: boolean;
  setDisabledAction?: (pending: boolean) => void;
  hideButtons?: boolean;
};

const AttendanceCard = ({
  staffToken,
  attendance,
  onUndoCheckin,
  onCheckin,
  onUpdateAttendance,
  disabledAction,
  setDisabledAction,
  hideButtons = false
}: AttendanceCardProps) => {
  const [loading, setLoading] = useState(false);
  const showUndoCheckin = attendance.checkin_date || attendance.checkin_request_date;
  const showCheckin = !attendance.checkin_date && !attendance.checkin_request_date;

  const copyToClipboard = () => {
    navigator.clipboard.writeText(attendance.invoice_id || '');
    notification.success({
      message: 'Código copiado'
    });
  };

  const handleCheckin = (attendance: Attendance) => {
    if (!attendance.attendance_id) return;

    setLoading(true);
    setDisabledAction?.(true);

    attendanceListService.checkinV2(attendance.attendance_id, staffToken).subscribe({
      next: response => {
        notification.success({
          message: `Check-in realizado com sucesso para ${attendance.name}`
        });
        onUpdateAttendance({ ...attendance, ...response });
        setLoading(false);
        setDisabledAction?.(false);
        onCheckin?.();
      },
      error: err => {
        const message =
          err?.data?.code === 'ERR_FBA403'
            ? 'Você não tem permissão para realizar esta ação.'
            : 'Erro ao realizar check-in. Tente novamente.';
        notification.error({ message });
        setLoading(false);
        setDisabledAction?.(false);
      }
    });
  };

  const handleUndoCheckin = (attendance: Attendance) => {
    if (!attendance.attendance_id) return;

    setLoading(true);

    setDisabledAction?.(true);

    attendanceListService.checkinV2(attendance.attendance_id, staffToken, true).subscribe({
      next: response => {
        notification.success({
          message: `Check-In desfeito para ${attendance.name}`
        });
        onUpdateAttendance({ ...attendance, ...response, checkin_date: null, checkin_request_date: null });
        setTimeout(() => {
          setDisabledAction?.(false);
          setLoading(false);
          onUndoCheckin?.();
        }, 1000);
      },
      error: err => {
        const message =
          err?.data?.code === 'ERR_FBA403'
            ? 'Você não tem permissão para realizar esta ação.'
            : 'Erro ao desfazer check-in. Tente novamente.';
        notification.error({ message });
        setLoading(false);
        setDisabledAction?.(false);
      }
    });
  };

  return (
    <div className={styles.container}>
      <div className={styles.section}>
        <p className={styles.text}>
          <span className={`${styles.text} ${styles.label}`}>Ingresso: </span>
          {attendance.ticket_name} - {attendance.lot_name}
        </p>
        <p className={styles.text}>
          <span className={`${styles.text} ${styles.label}`}>Cód. do Ingresso: </span> {attendance.invite_key}
        </p>
        {attendance.invoice_id && (
          <p className={styles.text}>
            <span className={`${styles.text} ${styles.label}`}>Fatura: </span>{' '}
            <Button icon={<CopyOutlined />} onClick={() => copyToClipboard()}>
              {attendance.invoice_id}
            </Button>
          </p>
        )}
      </div>
      <div className={styles.divider}></div>
      <div className={styles.section}>
        <p className={styles.text}>
          <span className={`${styles.text} ${styles.label}`}>Nome: </span>
          {attendance.name}
        </p>
        {attendance.document && (
          <p className={styles.text}>
            <span className={`${styles.text} ${styles.label}`}>CPF: </span>
            {attendance.document}
          </p>
        )}
        {!!attendance.custom_fields.length &&
          attendance.custom_fields.map(customField => (
            <p className={styles.text} key={customField.label}>
              <span className={`${styles.text} ${styles.label}`}>{customField.label}: </span>
              {customField.value}
            </p>
          ))}
      </div>
      <div className={styles.divider}></div>
      <div className={styles.section}>
        {(attendance.checkin_date || attendance.checkin_request_date) && (
          <p className={styles.text}>
            <span className={`${styles.text} ${styles.label}`}>Data do check-in: </span>
            {format((attendance.checkin_date ?? attendance.checkin_request_date) as Date, "dd/MM/yyyy 'às' HH:mm")}
          </p>
        )}
        {hideButtons ? null : (
          <>
            {showUndoCheckin && (
              <Button
                style={{ marginTop: '10px', marginBottom: '10px' }}
                type='default'
                block
                disabled={disabledAction}
                onClick={() => {
                  handleUndoCheckin(attendance);
                }}
                loading={loading}
              >
                {loading ? 'Processando...' : 'Desfazer Check-in'}
              </Button>
            )}
            {showCheckin && (
              <Button
                style={{ marginTop: '10px', marginBottom: '10px' }}
                type='default'
                block
                disabled={disabledAction}
                onClick={() => handleCheckin(attendance)}
                loading={loading}
              >
                {loading ? 'Processando...' : 'Fazer Check-in'}
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AttendanceCard;
